"use client";

import type { LucideIcon } from "@/components/icons";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { GREEN } from "@/lib/constants";
import { Network, Wifi, Zap } from "@/components/icons";

function FootprintIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray="3 2" />
      <line x1="3" y1="12" x2="6" y2="12" />
      <line x1="18" y1="12" x2="21" y2="12" />
      <line x1="12" y1="3" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="21" />
    </svg>
  );
}

type Requirement = {
  icon: LucideIcon | typeof FootprintIcon;
  label: string;
  detail: string;
  stat: string;
};

const REQUIREMENTS: Requirement[] = [
  {
    icon: FootprintIcon,
    label: "Dedicated space",
    detail: "Out of the way of bumps and damage",
    stat: "10 sq ft",
  },
  {
    icon: Network,
    label: "Wired internet via ethernet",
    detail: "Unlimited data, no caps",
    stat: "100+ Mbps",
  },
  {
    icon: Wifi,
    label: "Reliable 4G LTE signal",
    detail: "Minimum during peak hours",
    stat: "20+ Mbps",
  },
  {
    icon: Zap,
    label: "Nearby power socket",
    detail: "Extension cord included",
    stat: "Included",
  },
];

export default function RequirementsVisual() {
  const reduce = useReducedMotion();

  return (
    <div className="relative mx-auto w-full max-w-md">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-2xl"
        style={{
          background: "radial-gradient(ellipse at center, rgba(91,140,255,0.08), transparent 70%)",
        }}
      />

      <div
        className="relative rounded-2xl p-6 backdrop-blur-sm lg:p-7"
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          boxShadow:
            "0 1px 0 color-mix(in srgb, var(--foreground) 4%, transparent) inset, 0 24px 48px -24px rgba(0,0,0,0.4)",
        }}
      >
        <div
          className="mb-5 flex items-center gap-3 pb-4"
          style={{ borderBottom: "1px dashed var(--border)" }}
        >
          <div
            className="flex h-8 w-8 items-center justify-center rounded-md"
            style={{
              background: "var(--surface-3)",
              border: "1px solid var(--border)",
              color: "var(--foreground)",
            }}
          >
            <FootprintIcon size={14} />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="text-[11px] font-semibold tracking-[0.18em] uppercase"
              style={{ color: "var(--muted-foreground)" }}
            >
              Hosting Spec
            </div>
            <div
              className="text-sm leading-tight font-semibold"
              style={{ color: "var(--foreground)" }}
            >
              Minimum requirements
            </div>
          </div>
          <span
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium"
            style={{
              background: "rgba(91,140,255,0.10)",
              color: "var(--brand-green)",
              border: "1px solid rgba(91,140,255,0.25)",
            }}
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: GREEN }} />
            Eligible
          </span>
        </div>

        <ul className="space-y-2.5">
          {REQUIREMENTS.map((r, i) => {
            const Icon = r.icon;
            return (
              <motion.li
                key={r.label}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={scrollRevealViewport}
                transition={reduce ? undefined : { duration: 0.4, delay: i * 0.12 }}
                className="flex items-center gap-3 rounded-lg p-3"
                style={{
                  background: "var(--surface-3)",
                  border: "1px solid var(--border)",
                }}
              >
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
                  style={{
                    background: "var(--surface-1)",
                    border: "1px solid var(--border)",
                    color: "var(--foreground)",
                  }}
                >
                  <Icon size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div
                    className="text-sm leading-tight font-medium"
                    style={{ color: "var(--foreground)" }}
                  >
                    {r.label}
                  </div>
                  <div
                    className="mt-0.5 text-xs leading-snug"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    {r.detail}
                  </div>
                </div>
                <div
                  className="shrink-0 rounded-md px-2 py-1 text-xs font-semibold whitespace-nowrap"
                  style={{
                    background: "color-mix(in srgb, var(--foreground) 5%, transparent)",
                    color: "var(--foreground)",
                    border: "1px solid var(--border)",
                  }}
                >
                  {r.stat}
                </div>
              </motion.li>
            );
          })}
        </ul>

        <div
          className="mt-5 flex items-center justify-between pt-4 text-[11px]"
          style={{ borderTop: "1px dashed var(--border)", color: "var(--muted-foreground)" }}
        >
          <span className="font-mono tracking-tight">spec.v1</span>
          <span>Provided by Proxidize</span>
        </div>
      </div>
    </div>
  );
}
